import { useState } from 'react';
import { 
  ArrowLeft, 
  Mail, 
  Search, 
  FileText, 
  LifeBuoy, 
  HelpCircle,
  ChevronRight,
  Send
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';

interface HelpSupportPageProps {
  onBack: () => void;
  isLoggedIn?: boolean;
}

interface FAQ {
  category: string;
  question: string;
  answer: string;
}

const faqData: FAQ[] = [
  {
    category: 'Getting Started',
    question: 'How do I create my profile?',
    answer: 'Click on "Register" and follow our simple registration process. Add your photos, write about yourself, specify your preferences, and you\'re ready to find your perfect match!'
  },
  {
    category: 'Getting Started',
    question: 'What makes a good profile?',
    answer: 'A good profile has recent, clear photos (at least 3), detailed information about yourself, honest answers, and specific partner preferences. Profiles with verification badges get 3x more responses!'
  },
  {
    category: 'Getting Started',
    question: 'How does the matching algorithm work?',
    answer: 'Our AI analyzes over 50+ compatibility factors including education, profession, lifestyle, values, and preferences to suggest the most compatible matches. Premium members get advanced AI recommendations.'
  },
  {
    category: 'Safety & Privacy',
    question: 'How is my data protected?',
    answer: 'We use bank-grade encryption, never share your contact details without permission, and give you complete control over who can view your profile. Read our Privacy Policy for details.'
  },
  {
    category: 'Safety & Privacy',
    question: 'How do I report suspicious profiles?',
    answer: 'Click the three dots on any profile and select "Report". Our team reviews all reports within 24 hours. You can also block users to prevent them from contacting you.'
  },
  {
    category: 'Safety & Privacy',
    question: 'What is profile verification?',
    answer: 'We offer Phone, Email, Photo, and Government ID verification. Verified profiles are marked with badges and rank higher in search results. Verification builds trust and increases response rates.'
  },
  {
    category: 'Messaging',
    question: 'Why can\'t I read some messages?',
    answer: 'Free members can view interests but need a Premium plan to read and reply to messages. This ensures serious commitment from both parties. Upgrade to unlock unlimited messaging!'
  },
  {
    category: 'Messaging',
    question: 'How do I start a conversation?',
    answer: 'Send an "Interest" to profiles you like. If they accept, you can start chatting! Premium members can send personalized messages. Be respectful, genuine, and ask about their interests.'
  },
  {
    category: 'Payments & Plans',
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit/debit cards, UPI, net banking, and digital wallets. All transactions are secured with 256-bit SSL encryption.'
  },
  {
    category: 'Payments & Plans',
    question: 'Can I get a refund?',
    answer: 'We offer a 7-day money-back guarantee if you haven\'t used any premium features. Contact our support team with your order details to request a refund.'
  },
  {
    category: 'Features',
    question: 'What is Premium membership?',
    answer: 'Premium members get unlimited messaging, advanced search filters, profile boosting, video calls, and priority customer support. Plans start from ₹999/month.'
  },
  {
    category: 'Features',
    question: 'How does profile verification work?',
    answer: 'Upload your government ID or take a live selfie. Our team verifies it within 24 hours. Verified profiles get a blue badge and appear higher in search results.'
  },
  {
    category: 'Account',
    question: 'How do I delete my account?',
    answer: 'Go to Settings → Account Settings → Delete Account. Note that this action is permanent and all your data will be deleted within 30 days.'
  },
  {
    category: 'Account',
    question: 'Can I hide my profile temporarily?',
    answer: 'Yes! Go to Settings → Privacy → Hide Profile. Your profile won\'t appear in searches but your existing matches can still see it.'
  },
  {
    category: 'Account',
    question: 'How do I change my preferences?',
    answer: 'Go to Settings → Partner Preferences and update your criteria. Changes are saved automatically and affect future match suggestions.'
  },
  {
    category: 'Account',
    question: 'I forgot my password, what should I do?',
    answer: 'Click "Forgot Password" on the login page, enter your email/phone, and we\'ll send you a reset link. You can also reset it using OTP.'
  },
  {
    category: 'Account',
    question: 'Can I change my mobile number?',
    answer: 'Yes, go to Settings → Account Settings → Change Phone Number. You\'ll need to verify the new number with an OTP.'
  },
  {
    category: 'Account',
    question: 'How do I verify my email address?',
    answer: 'Check your inbox for a verification email. Click the link to verify. If you didn\'t receive it, go to Settings → Account Settings → Resend Verification Email.'
  }
];

const categories = [
  { id: 'all', label: 'All', icon: FileText },
  { id: 'Getting Started', label: 'Getting Started', icon: HelpCircle },
  { id: 'Safety & Privacy', label: 'Safety & Privacy', icon: LifeBuoy },
  { id: 'Messaging', label: 'Messaging', icon: Send },
  { id: 'Payments & Plans', label: 'Payments & Plans', icon: FileText },
  { id: 'Features', label: 'Features', icon: FileText },
  { id: 'Account', label: 'Account', icon: FileText }
];

export default function HelpSupportPage({ onBack, isLoggedIn }: HelpSupportPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredFAQs = faqData.filter(faq => {
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handleStartChat = () => {
    window.open('https://wa.me/918001234567', '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-600 to-rose-600 text-white px-4 py-4 shadow-md sticky top-0 z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={onBack}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="font-semibold text-lg">Help & Support</h1>
                <p className="text-sm text-white/90">We're here to help you</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              className="bg-white text-pink-600 border-white hover:bg-white/90"
            >
              <LifeBuoy className="h-4 w-4 mr-2" />
              24/7 Support
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Support Options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Live Chat - Hidden */}
          {/* <Card className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start gap-4">
              <div className="bg-green-100 rounded-full p-3">
                <Send className="h-6 w-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Live Chat</h3>
                <p className="text-sm text-gray-600 mb-3">Available 24/7</p>
                <Button 
                  className="w-full bg-pink-500 hover:bg-pink-600 text-white"
                  onClick={handleStartChat}
                >
                  Start Chat
                </Button>
              </div>
            </div>
          </Card> */}

          {/* Call Us - Hidden */}
          {/* <Card className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 rounded-full p-3">
                <Send className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Call Us</h3>
                <p className="text-sm text-gray-600 mb-3">Mon-Sat, 9 AM - 9 PM</p>
                <a 
                  href="tel:+918001234567"
                  className="block text-center text-gray-900 font-medium hover:text-pink-600 transition-colors"
                >
                  +91 1800-123-4567
                </a>
              </div>
            </div>
          </Card> */}

          {/* Email Support */}
          <Card className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start gap-4">
              <div className="bg-purple-100 rounded-full p-3">
                <Mail className="h-6 w-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Email Support</h3>
                <p className="text-sm text-gray-600 mb-3">Response in 24 hours</p>
                <a 
                  href="mailto:support@atootbandan.com"
                  className="block text-center text-gray-900 font-medium hover:text-pink-600 transition-colors text-sm"
                >
                  support@atootbandan.com
                </a>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs for different sections */}
        <Tabs defaultValue="faqs" className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-white shadow-sm">
            <TabsTrigger value="faqs" className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4" />
              FAQs
            </TabsTrigger>
            <TabsTrigger value="guides" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Guides
            </TabsTrigger>
            <TabsTrigger value="support-ticket" className="flex items-center gap-2">
              <LifeBuoy className="h-4 w-4" />
              Support Ticket
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Contact
            </TabsTrigger>
          </TabsList>

          <TabsContent value="faqs" className="mt-6 space-y-6">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search for answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 py-6 text-base bg-white border-gray-200"
              />
            </div>

            {/* Category Pills */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${
                    selectedCategory === cat.id
                      ? 'bg-pink-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* FAQ List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Frequently Asked Questions</h2>
                <span className="text-sm text-gray-600">{filteredFAQs.length} questions found</span>
              </div>

              {filteredFAQs.length === 0 ? (
                <Card className="p-8 text-center">
                  <HelpCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No FAQs found matching your search.</p>
                </Card>
              ) : (
                <Accordion type="single" collapsible className="space-y-3">
                  {filteredFAQs.map((faq, index) => (
                    <AccordionItem 
                      key={index} 
                      value={`item-${index}`}
                      className="bg-white border border-gray-200 rounded-lg px-4 shadow-sm"
                    >
                      <AccordionTrigger className="hover:no-underline py-4">
                        <span className="text-left">{faq.question}</span>
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600 pb-4">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          </TabsContent>

          <TabsContent value="guides" className="mt-6">
            <Card className="p-8">
              <h3 className="text-lg font-semibold mb-4">User Guides</h3>
              <div className="space-y-3">
                <button className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-pink-600" />
                    <span className="font-medium">Getting Started Guide</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
                <button className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-pink-600" />
                    <span className="font-medium">Profile Optimization Tips</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
                <button className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-pink-600" />
                    <span className="font-medium">Safety & Security Guide</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
                <button className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-pink-600" />
                    <span className="font-medium">Premium Features Guide</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="support-ticket" className="mt-6">
            <Card className="p-8">
              <h3 className="text-lg font-semibold mb-4">Create Support Ticket</h3>
              <p className="text-gray-600 mb-6">
                Can't find what you're looking for? Submit a support ticket and our team will get back to you within 24 hours.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <Input type="text" placeholder="Brief description of your issue" className="bg-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white">
                    <option>Account Issues</option>
                    <option>Payment & Billing</option>
                    <option>Technical Problems</option>
                    <option>Profile & Verification</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white resize-none"
                    rows={6}
                    placeholder="Please provide detailed information about your issue..."
                  />
                </div>
                <Button className="w-full bg-pink-600 hover:bg-pink-700 text-white py-6">
                  Submit Ticket
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="contact" className="mt-6">
            <Card className="p-8">
              <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
              <div className="space-y-6">
                {/* Phone Support - Hidden */}
                {/* <div className="flex items-start gap-4">
                  <div className="bg-pink-100 rounded-full p-3">
                    <Send className="h-6 w-6 text-pink-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Phone Support</h4>
                    <p className="text-gray-600 text-sm mb-2">Mon-Sat: 9 AM - 9 PM</p>
                    <a href="tel:+918001234567" className="text-pink-600 font-medium hover:text-pink-700">
                      +91 1800-123-4567
                    </a>
                  </div>
                </div> */}
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 rounded-full p-3">
                    <Mail className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Email Support</h4>
                    <p className="text-gray-600 text-sm mb-2">Response within 24 hours</p>
                    <a href="mailto:support@atootbandan.com" className="text-pink-600 font-medium hover:text-pink-700">
                      support@atootbandan.com
                    </a>
                  </div>
                </div>
                {/* Live Chat - Hidden */}
                {/* <div className="flex items-start gap-4">
                  <div className="bg-green-100 rounded-full p-3">
                    <Send className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Live Chat</h4>
                    <p className="text-gray-600 text-sm mb-2">Available 24/7</p>
                    <Button 
                      onClick={handleStartChat}
                      variant="link" 
                      className="text-pink-600 font-medium hover:text-pink-700 p-0 h-auto"
                    >
                      Start a conversation
                    </Button>
                  </div>
                </div> */}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}